// MidCallPlaybookSidebar.jsx — persistent mid-call reference for the
// consultant while a call is active. Mounted globally (same pattern as
// RingingPopup) and driven by WebSocket events `call_answered_playbook`
// (opens) and `call_ended` (dismisses).
//
// Contents mirror the full archetype playbook: opening line, checklist,
// discovery questions, all objections, close, DISC tone, do-not-say.
// Collapsible, dismissable, position: fixed on the right edge.
import React, { useEffect, useState, useMemo, useCallback } from 'react';
import axios from 'axios';
import { useParams, useLocation } from 'react-router-dom';
import { useRingingSocket } from '../hooks/useRingingSocket';
import { useAuth } from '../contexts/AuthContext';
import {
  BookOpen, X, ChevronLeft, ChevronRight, Sparkles,
  CheckSquare, HelpCircle, ShieldAlert, Flag, Users2, AlertOctagon, Coffee, Undo2,
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const _SectionCard = ({ title, icon: Icon, children }) => (
  <div className="mb-3">
    <div className="text-slate-500 text-[10px] uppercase tracking-widest font-semibold flex items-center gap-1 mb-1">
      {Icon ? <Icon className="w-3 h-3" /> : null}{title}
    </div>
    <div className="text-slate-700 text-xs leading-relaxed">{children}</div>
  </div>
);

const MidCallPlaybookSidebar = () => {
  const { user } = useAuth();
  const params = useParams();
  const location = useLocation();
  const routeDomain = params.domainId
    || (location.pathname.match(/\/admin\/domains\/([0-9a-f-]{36})/i) || [])[1];
  const domainId = user?.role === 'master_admin' ? routeDomain : user?.domain_id;
  const { callState } = useRingingSocket(domainId);
  const [current, setCurrent] = useState(null);
  const [collapsed, setCollapsed] = useState(false);
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    if (!callState) return;
    if ((callState.type === 'call_answered_playbook' || callState.type === 'playbook_hint_corrected') && callState.playbook) {
      setCurrent(callState);
      setCollapsed(false);
      setDismissed(false);
    } else if (callState.type === 'call_ended') {
      setCurrent(null);
    }
  }, [callState]);

  // Listen for a manual `open-playbook-sidebar` custom event fired from
  // the call detail page ("Show playbook sidebar" button on past calls).
  useEffect(() => {
    const onOpen = (e) => {
      const detail = e.detail || {};
      if (detail.playbook) {
        setCurrent({ type: 'call_answered_playbook', playbook: detail.playbook, _manual: true });
        setCollapsed(false);
        setDismissed(false);
      }
    };
    window.addEventListener('open-playbook-sidebar', onOpen);
    return () => window.removeEventListener('open-playbook-sidebar', onOpen);
  }, []);

  const sections = useMemo(() => current?.playbook?.sections || {}, [current]);
  const archetype = current?.playbook?.archetype;
  const snoozeCallId = current?.yabbit_call_id || null;
  const [snoozes, setSnoozes] = useState({}); // { [step_index]: {reason, id} }

  // Reset snoozes when a new call opens the sidebar
  useEffect(() => {
    setSnoozes({});
    if (!snoozeCallId) return;
    (async () => {
      try {
        const { data } = await axios.get(`${API}/calls/${snoozeCallId}/step-snoozes`);
        const map = {};
        (data.snoozes || []).forEach((s) => { map[s.step_index] = s; });
        setSnoozes(map);
      } catch { /* pre-hydration is best-effort */ }
    })();
  }, [snoozeCallId]);

  const toggleSnooze = useCallback(async (stepIndex) => {
    if (!snoozeCallId) return;
    const existing = snoozes[stepIndex];
    try {
      if (existing) {
        await axios.delete(`${API}/calls/${snoozeCallId}/step-snoozes/${stepIndex}`);
        setSnoozes((prev) => { const n = { ...prev }; delete n[stepIndex]; return n; });
      } else {
        const { data } = await axios.post(`${API}/calls/${snoozeCallId}/step-snoozes`, {
          step_index: stepIndex,
          reason: 'attempted_but_deflected',
        });
        setSnoozes((prev) => ({ ...prev, [stepIndex]: data }));
      }
    } catch (e) { /* swallow — button will just look un-toggled */ }
  }, [snoozeCallId, snoozes]);

  if (!current || dismissed) return null;

  // Collapsed state — thin edge tab
  if (collapsed) {
    return (
      <button
        onClick={() => setCollapsed(false)}
        className="fixed right-0 top-1/2 -translate-y-1/2 z-40 bg-teal-600 hover:bg-teal-500 text-white px-2 py-6 rounded-l-lg shadow-lg flex flex-col items-center gap-1"
        data-testid="mid-call-sidebar-expand"
        title="Show playbook"
      >
        <ChevronLeft className="w-4 h-4" />
        <BookOpen className="w-4 h-4" />
        <span className="text-[10px] font-semibold uppercase tracking-widest [writing-mode:vertical-rl]">Playbook</span>
      </button>
    );
  }

  return (
    <div
      className="fixed right-0 top-0 bottom-0 z-40 w-[380px] max-w-[85vw] bg-white shadow-2xl border-l-4 border-teal-500 overflow-hidden flex flex-col"
      data-testid="mid-call-sidebar"
    >
      {/* Header */}
      <div className="bg-gradient-to-r from-teal-600 to-cyan-600 text-white px-4 py-3 flex items-center gap-2 shrink-0">
        <Sparkles className="w-4 h-4 shrink-0" />
        <div className="flex-1 min-w-0">
          <div className="text-[10px] uppercase tracking-widest opacity-90 flex items-center gap-1.5">
            Live Playbook
            {current.type === 'playbook_hint_corrected' && (
              <span className="bg-amber-400 text-amber-900 px-1.5 py-0.5 rounded text-[9px] font-bold uppercase tracking-widest" title={`Was predicted as ${current.was_archetype}`}>Corrected</span>
            )}
            {current._manual && (
              <span className="bg-white/20 text-white px-1.5 py-0.5 rounded text-[9px] font-bold uppercase tracking-widest">Review</span>
            )}
          </div>
          <div className="font-bold text-sm truncate">{archetype}</div>
        </div>
        <button
          onClick={() => setCollapsed(true)}
          className="p-1 rounded hover:bg-white/10 shrink-0"
          data-testid="mid-call-sidebar-collapse"
          title="Collapse"
        >
          <ChevronRight className="w-4 h-4" />
        </button>
        <button
          onClick={() => setDismissed(true)}
          className="p-1 rounded hover:bg-white/10 shrink-0"
          data-testid="mid-call-sidebar-dismiss"
          title="Dismiss"
        >
          <X className="w-4 h-4" />
        </button>
      </div>

      {/* Body */}
      <div className="flex-1 overflow-y-auto p-4">
        {sections.opening_line && (
          <_SectionCard title="Opening line" icon={BookOpen}>
            <blockquote className="border-l-2 border-teal-500 pl-2.5 italic text-slate-800">
              &ldquo;{sections.opening_line}&rdquo;
            </blockquote>
          </_SectionCard>
        )}

        {Array.isArray(sections.checklist) && sections.checklist.length > 0 && (
          <_SectionCard title="Checklist" icon={CheckSquare}>
            <ol className="list-decimal pl-4 space-y-1">
              {sections.checklist.map((step, i) => {
                const snoozed = !!snoozes[i];
                return (
                  <li key={i} className={snoozed ? 'text-slate-400 line-through' : ''}>
                    <div className="flex items-start justify-between gap-2">
                      <span className="flex-1">{step}</span>
                      {snoozeCallId && (
                        <button
                          type="button"
                          onClick={() => toggleSnooze(i)}
                          className={`shrink-0 inline-flex items-center gap-1 rounded px-1.5 py-0.5 text-[10px] font-semibold border transition-colors no-underline ${
                            snoozed
                              ? 'bg-amber-100 border-amber-300 text-amber-800 hover:bg-amber-200'
                              : 'bg-white border-slate-200 text-slate-500 hover:bg-amber-50 hover:border-amber-300 hover:text-amber-800'
                          }`}
                          data-testid={`snooze-step-${i}`}
                          title={snoozed ? 'Un-flag — I did land this step' : 'Attempted but caller deflected'}
                        >
                          {snoozed ? <Undo2 className="w-3 h-3" /> : <Coffee className="w-3 h-3" />}
                          {snoozed ? 'Un-snooze' : 'Deflected'}
                        </button>
                      )}
                    </div>
                  </li>
                );
              })}
            </ol>
          </_SectionCard>
        )}

        {Array.isArray(sections.discovery_questions) && sections.discovery_questions.length > 0 && (
          <_SectionCard title="Green Brain discovery" icon={HelpCircle}>
            <ul className="list-disc pl-4 space-y-0.5">
              {sections.discovery_questions.map((q, i) => (<li key={i}>{q}</li>))}
            </ul>
          </_SectionCard>
        )}

        {Array.isArray(sections.objection_handling) && sections.objection_handling.length > 0 && (
          <_SectionCard title="Objections" icon={ShieldAlert}>
            <div className="space-y-1.5">
              {sections.objection_handling.map((o, i) => (
                <div key={i} className="bg-amber-50 border border-amber-200 rounded p-2">
                  <div className="text-amber-800 text-[10px] font-mono">{o.objection}</div>
                  <div className="text-slate-700 text-xs mt-0.5">→ {o.response}</div>
                </div>
              ))}
            </div>
          </_SectionCard>
        )}

        {sections.close_or_next_step && (
          <_SectionCard title="Close / next step" icon={Flag}>
            <p>{sections.close_or_next_step}</p>
          </_SectionCard>
        )}

        {sections.disc_notes && Object.keys(sections.disc_notes).length > 0 && (
          <_SectionCard title="DISC tone" icon={Users2}>
            <div className="grid grid-cols-2 gap-1.5">
              {Object.entries(sections.disc_notes).map(([disc, note]) => (
                <div key={disc} className="bg-slate-50 border border-slate-200 rounded p-1.5">
                  <div className="text-teal-700 font-mono text-[10px]">{disc}</div>
                  <div className="text-slate-700 text-[11px]">{note}</div>
                </div>
              ))}
            </div>
          </_SectionCard>
        )}

        {Array.isArray(sections.do_not_say) && sections.do_not_say.length > 0 && (
          <_SectionCard title="Do NOT say" icon={AlertOctagon}>
            <ul className="list-disc pl-4 space-y-0.5 text-rose-800">
              {sections.do_not_say.map((x, i) => (<li key={i}>{x}</li>))}
            </ul>
          </_SectionCard>
        )}
      </div>

      <div className="text-[10px] text-slate-400 px-4 py-2 border-t border-slate-100 flex items-center justify-between shrink-0">
        <span>Playbook v{current.playbook?.playbook_version || '?'} · auto-dismisses on hangup</span>
      </div>
    </div>
  );
};

export default MidCallPlaybookSidebar;
